import { create } from 'zustand';
import type { Interpretation, AIProvider } from '../types';

interface InterpretationState {
  // Current interpretation being generated/viewed
  currentInterpretation: Interpretation | null;
  interpretation: string;      // live streaming text (was: streamingText)
  thinkingProcess: string;

  // Loading & status
  isGenerating: boolean;       // was: isLoading
  error: string | null;
  progress: number; // 0-100

  // History of interpretations in current session
  interpretations: Interpretation[];

  // Primary actions
  setCurrentInterpretation: (interpretation: Interpretation | null) => void;
  setInterpretation: (text: string) => void;      // set full text
  appendChunk: (chunk: string) => void;           // was: appendStreamingText
  setThinkingProcess: (thinking: string) => void;
  appendThinkingProcess: (chunk: string) => void;
  setIsGenerating: (loading: boolean) => void;    // was: setIsLoading
  setError: (error: string | null) => void;
  /** Accepts a number or an updater function (prev) => next */
  setProgress: (progress: number | ((prev: number) => number)) => void;
  addInterpretation: (interpretation: Interpretation) => void;
  removeInterpretation: (id: string) => void;
  clearInterpretations: () => void;
  clear: () => void;                              // was: reset

  // Backward-compat method aliases (kept for any callers using old names)
  appendStreamingText: (chunk: string) => void;
  reset: () => void;
  setIsLoading: (loading: boolean) => void;
  setStreamingText: (text: string) => void;

  // Finalize streaming into an Interpretation record
  finalizeInterpretation: (
    provider: AIProvider,
    model: string,
    prompt: string,
    tokens?: { input: number; output: number }
  ) => Interpretation;
}

const generateId = () => Math.random().toString(36).substring(2, 11);

export const useInterpretationStore = create<InterpretationState>((set, get) => ({
  currentInterpretation: null,
  interpretation: '',
  thinkingProcess: '',
  isGenerating: false,
  error: null,
  progress: 0,
  interpretations: [],

  // --- Primary actions ---

  setCurrentInterpretation: (currentInterpretation) =>
    set({ currentInterpretation }),

  setInterpretation: (text) => set({ interpretation: text }),

  appendChunk: (chunk) =>
    set((state) => ({ interpretation: state.interpretation + chunk })),

  setThinkingProcess: (thinking) => set({ thinkingProcess: thinking }),

  appendThinkingProcess: (chunk) =>
    set((state) => ({ thinkingProcess: state.thinkingProcess + chunk })),

  setIsGenerating: (isGenerating) => set({ isGenerating }),

  setError: (error) => set({ error }),

  setProgress: (progressOrFn) =>
    set((state) => {
      const next =
        typeof progressOrFn === 'function'
          ? progressOrFn(state.progress)
          : progressOrFn;
      return { progress: Math.min(100, Math.max(0, next)) };
    }),

  addInterpretation: (interp) =>
    set((state) => ({
      interpretations: [interp, ...state.interpretations],
      currentInterpretation: interp,
    })),

  removeInterpretation: (id) =>
    set((state) => ({
      interpretations: state.interpretations.filter((i) => i.id !== id),
      currentInterpretation:
        state.currentInterpretation?.id === id ? null : state.currentInterpretation,
    })),

  clearInterpretations: () =>
    set({ interpretations: [], currentInterpretation: null }),

  clear: () =>
    set({
      currentInterpretation: null,
      interpretation: '',
      thinkingProcess: '',
      isGenerating: false,
      error: null,
      progress: 0,
    }),

  // --- Backward-compat aliases ---
  appendStreamingText: (chunk) => get().appendChunk(chunk),
  reset: () => get().clear(),
  setIsLoading: (loading) => get().setIsGenerating(loading),
  setStreamingText: (text) => get().setInterpretation(text),

  // --- Finalize ---

  finalizeInterpretation: (provider, model, prompt, tokens) => {
    const { interpretation, thinkingProcess } = get();

    const record: Interpretation = {
      id: generateId(),
      content: interpretation,
      thinkingProcess: thinkingProcess || undefined,
      provider,
      model,
      prompt,
      generatedAt: new Date(),
      tokens,
    };

    set((state) => ({
      interpretations: [record, ...state.interpretations],
      currentInterpretation: record,
      interpretation: '',
      thinkingProcess: '',
      isGenerating: false,
      progress: 100,
    }));

    return record;
  },
}));
